import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import Breadcrumb from "../../components/Breadcrumb";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";

import useRepartitionCompetiteursPage from "./hooks/useRepartitionCompetiteursPage";

import GlobalAssignBar from "./components/GlobalAssignBar";
import RepartitionFilters from "./components/RepartitionFilters";
import RepartitionHeader from "./components/RepartitionHeader";
import RepartitionTable from "./components/RepartitionTable";
import RepartitionToolbar from "./components/RepartitionToolbar";

import DistributionDetailsModal from "./DistributionDetailsModal";
import BracketOrderModal from "./BracketOrderModal";

import { isBracketFormula } from "./utils/repartitionUtils";

const RepartitionCompetiteursPage = () => {
  useScrollToTop();

  const { id } = useParams();

  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState(null);

  const [isBracketModalOpen, setIsBracketModalOpen] = useState(false);
  const [distToEdit, setDistToEdit] = useState(null);

  const repartition = useRepartitionCompetiteursPage({
    competitionId: id,
  });

  const {
    competition,
    country,
    loading,

    distributions,
    competiteurs,

    filters,
    updateFilter,
    resetFilters,
    options,

    selectedIds,
    selectedCount,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
    clearSelection,

    pageRows,
    page,
    perPage,
    total,
    totalPages,
    setPage,
    setPerPage,

    sortBy,
    sortDir,
    toggleSortDist,

    assignForm,
    setAssignForm,
    clearingIds,

    actions,
  } = repartition;

  const openDistribution = (distributionId) => {
    const distribution = distributions.find(
      (item) => String(item.id) === String(distributionId),
    );

    if (!distribution) return;

    setShowDistributionModal(false);
    setIsBracketModalOpen(false);

    if (isBracketFormula(distribution.formule_repartition)) {
      setDistToEdit(distribution);
      setIsBracketModalOpen(true);
      return;
    }

    setSelectedDistribution(distribution);
    setShowDistributionModal(true);
  };

  const handleDeleteDistribution = async (distribution) => {
    const ok = await actions.deleteExistingDistribution(distribution);

    if (ok) {
      setShowDistributionModal(false);
      setIsBracketModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-[140px] flex min-h-[55vh] items-center justify-center px-4 text-center text-slate-600 dark:text-slate-300">
        Chargement des répartitions…
      </div>
    );
  }

  return (
    <main className="mt-[130px] min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 px-3 pb-10 text-slate-950 dark:bg-slate-950 dark:text-white sm:px-4 lg:px-6">
      <ScrollIndicator />

      <RepartitionHeader competition={competition} />

      <div className="mx-auto mb-4 w-full max-w-[1680px]">
        <Breadcrumb competitionName={competition?.nom_competition} />
      </div>

      <div className="mx-auto mb-4 w-full max-w-[1680px]">
        <RepartitionToolbar
          total={total}
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
        />
      </div>

      <div className="relative z-30 mx-auto mb-4 w-full max-w-[1680px] overflow-visible">
        <RepartitionFilters
          filters={filters}
          updateFilter={updateFilter}
          options={options}
          onReset={resetFilters}
        />
      </div>

      <div className="relative z-0 mx-auto w-full max-w-[1680px]">
        <RepartitionTable
          rows={pageRows}
          distributions={distributions}
          selectedIds={selectedIds}
          toggleSelect={toggleSelect}
          allVisibleSelected={allVisibleSelected}
          toggleSelectAllVisible={toggleSelectAllVisible}
          onOpenDistribution={openDistribution}
          page={page}
          perPage={perPage}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          sortBy={sortBy}
          sortDir={sortDir}
          onToggleSortDist={toggleSortDist}
          onClearCompetiteur={actions.clearCompetiteur}
          clearingIds={clearingIds}
        />

        <GlobalAssignBar
          form={assignForm}
          setForm={setAssignForm}
          selectedCount={selectedCount}
          onSubmit={actions.createDistribution}
        />
      </div>

      <DistributionDetailsModal
        isOpen={showDistributionModal}
        onClose={() => setShowDistributionModal(false)}
        distribution={selectedDistribution}
        allCompetiteurs={competiteurs}
        onDeleteDistribution={handleDeleteDistribution}
        cate_country={country}
        onSaved={actions.refreshAfterSave}
      />

      <BracketOrderModal
        isOpen={isBracketModalOpen}
        onClose={() => setIsBracketModalOpen(false)}
        distribution={distToEdit}
        allCompetiteurs={competiteurs}
        onSaved={actions.refreshAfterSave}
        onDeleteDistribution={handleDeleteDistribution}
      />

      <ToastContainer position="bottom-right" />
    </main>
  );
};

export default RepartitionCompetiteursPage;
